import { useInputControl } from '@conform-to/react'
import React, { useEffect, useId, useState } from 'react'
import { Checkbox, type CheckboxProps } from '~/components/ui/checkbox'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { Textarea } from '~/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectProps, SelectTrigger, SelectValue } from '~/components/ui/select'

export type ListOfErrors = Array<string | null | undefined> | null | undefined

export function ErrorList({
	id,
	errors,
}: {
	errors?: ListOfErrors
	id?: string
}) {
	const errorsToRender = errors?.filter(Boolean)
	if (!errorsToRender?.length) return null
	return (
		<ul id={id} className="flex flex-col gap-1">
			{errorsToRender.map(e => (
				<li key={e} className="text-[10px] text-foreground-destructive">
					{e}
				</li>
			))}
		</ul>
	)
}

export function Field({
	labelProps,
	inputProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps:  React.InputHTMLAttributes<HTMLInputElement> & { ref?: React.Ref<HTMLInputElement> }
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	const hasErrors = errors?.length && errors.length > 0
	
	return (
		<div className={`grid gap-3 ${className ?? ''}`}>
			<Label
				htmlFor={id}
				{...labelProps}
				className={hasErrors ? 'text-red-600' : labelProps.className}
			/>
			<Input
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				{...inputProps}
				className={hasErrors ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : inputProps.className}
			/>
			<div className="min-h-[19px] px-4 pb-1">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function TextareaField({
	labelProps,
	textareaProps,
	errors,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	textareaProps: React.TextareaHTMLAttributes<HTMLTextAreaElement>
	errors?: ListOfErrors
	className?: string
}) {
	const fallbackId = useId()
	const id = textareaProps.id ?? textareaProps.name ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	const hasErrors = errors?.length && errors.length > 0
	
	return (
		<div className={`grid gap-3 ${className ?? ''}`}>
			<Label
				htmlFor={id}
				{...labelProps}
				className={hasErrors ? 'text-red-600' : labelProps.className}
			/>
			<Textarea
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				{...textareaProps}
				className={hasErrors ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : textareaProps.className}
			/>
			<div className="min-h-[32px] px-4 pb-3 pt-1">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function CheckboxField({
	labelProps,
	buttonProps,
	errors,
	className,
}: {
	labelProps: JSX.IntrinsicElements['label']
	buttonProps: CheckboxProps & {
		name: string
		form: string
		value?: string
	}
	errors?: ListOfErrors
	className?: string
}) {
  const { key, defaultChecked, ...checkboxProps } = buttonProps
	const fallbackId = useId()
  const checkedValue = buttonProps.value ?? 'on'
	const input = useInputControl({
		key,
		name: buttonProps.name,
		formId: buttonProps.form,
		initialValue: defaultChecked ? checkedValue : undefined,
  })
  const id = buttonProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
  return (
		<div className={className}>
			<div className="flex gap-2">
				<Checkbox
					{...checkboxProps}
					id={id}
					aria-invalid={errorId ? true : undefined}
					aria-describedby={errorId}
					checked={input.value === checkedValue}
					onCheckedChange={state => {
						input.change(state.valueOf() ? checkedValue : '')
						buttonProps.onCheckedChange?.(state)
					}}
					onFocus={event => {
						input.focus()
						buttonProps.onFocus?.(event)
					}}
					onBlur={event => {
						input.blur()
						buttonProps.onBlur?.(event)
					}}
					type="button"
				/>
				<label
					htmlFor={id}
					{...labelProps}
					className="self-center text-sm text-muted-foreground"
				/>
			</div>
			<div className="px-4 pb-3 pt-1">
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}


export type SelectOption = {
  value: string
  label: string
}

export function SelectField({
  labelProps,
  selectProps,
  options,
  errors,
  className,
}: {
  labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
  selectProps: SelectProps
  options: SelectOption[]
  errors?: ListOfErrors
  className?: string
}) {
  const fallbackId = useId()
  const id = selectProps.name ?? fallbackId
  const errorId = errors?.length ? `${id}-error` : undefined
  const hasErrors = errors?.length && errors.length > 0

  return (
    <div className={`grid gap-3 ${className ?? ''}`}>
      <Label
        htmlFor={id}
        {...labelProps}
        className={hasErrors ? 'text-red-600' : labelProps.className}
      />
      <Select
        defaultValue={selectProps.defaultValue}
        {...selectProps}
      >
        <SelectTrigger
          id={id}
          aria-invalid={errorId ? true : undefined}
          aria-describedby={errorId}
          className={hasErrors ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : undefined}
        >
          <SelectValue placeholder={selectProps.placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div className="min-h-[19px] px-4 pb-1">
        {errorId ? <ErrorList id={errorId} errors={errors} /> : null}
      </div>
    </div>
  )
}

type CheckboxOption = {
  label: string
  value: string
}

type CheckboxFieldArrayProps = {
  labelProps: JSX.IntrinsicElements['label']
  buttonProps: CheckboxProps & { name: string; form: string; value?: string[] }
  options: CheckboxOption[]
  errors?: ListOfErrors
  className?: string
}

export function CheckboxFieldArray({
  labelProps,
  buttonProps,
  options,
  errors,
  className,
}: CheckboxFieldArrayProps) {
  const fallbackId = useId()
  const input = useInputControl({
    name: buttonProps.name,
    formId: buttonProps.form,
  })

  const [checkedValues, setCheckedValues] = useState<string[]>(
    buttonProps.value || [],
  )

  useEffect(() => {
    if (buttonProps.value) {
      setCheckedValues(buttonProps.value)
    }
  }, [buttonProps.value])

  const id = buttonProps.id ?? buttonProps.name ?? fallbackId
  const errorId = errors?.length ? `${buttonProps.name}-error` : undefined

  const handleCheckboxChange = (value: string, checked: boolean) => {
    const newValues = checked
      ? [...checkedValues, value]
      : checkedValues.filter(v => v !== value)
    setCheckedValues(newValues)
    input.change(newValues)
  }

  return (
    <div className={className}>
      <Label htmlFor={id} {...labelProps} />
      <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {options.map(option => {
          const id = `${buttonProps.name}-${option.value}`
          return (
            <div key={option.value} className="flex items-center">
              <Checkbox
                {...buttonProps}
                id={id}
                value={option.value}
                checked={checkedValues.includes(option.value)}
                onCheckedChange={checked =>
                  handleCheckboxChange(option.value, checked as boolean)
                }
                aria-invalid={errorId ? true : undefined}
                aria-describedby={errorId}
                onFocus={event => {
                  input.focus()
                  buttonProps.onFocus?.(event)
                }}
                onBlur={event => {
                  input.blur()
                  buttonProps.onBlur?.(event)
                }}
                type="button"
              />
              <label
                htmlFor={id}
                className="ml-2 text-sm font-medium text-gray-700"
              >
                {option.label}
              </label>
            </div>
          )
        })}
      </div>
      <div className="px-4 pb-3 pt-1">
        {errorId ? <ErrorList id={errorId} errors={errors} /> : null}
      </div>
    </div>
  )
}
